'use client';

/**
 * AskQuickAnswerCard — slots 1+2 of the flipped answer, as a card.
 *
 * The fast pair: the top source's own short answer (slot 1) with the
 * long reading beneath it (slot 2), plus a source caption. Placed by
 * the `ask_compose` preset (klyde_edge composition/modes/ask.py);
 * reads the compose store — no props plumbing, the preset only places.
 *
 * `content_answer` is deliberately NOT part of the flip: the fast pair
 * derives from the same source doc, so mounting both renders the same
 * text twice.
 *
 * @see klyde_edge/ui_config/composition/cards/ask.py — @card declaration
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { useAskLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { ProseBlock } from './prose-card';

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

export function AskQuickAnswerCard() {
  const compose = useAgentCanvasStore((s) => s.compose);
  const view = compose?.view ?? null;

  // The clean view's slots as the shim's knowledge payload exposed them:
  //   short_answer = quick.short OR synthesis ; long_answer = synthesis ;
  //   summary.fast = the doc's own short/long.
  const shortAnswer = str(view?.quick?.short) ?? str(view?.synthesis);
  const longAnswer = str(view?.synthesis);
  const fastShort = str(view?.quick?.short);
  const fastLong = str(view?.quick?.long);
  const llmDone = str(view?.llm_status) === 'completed';
  // Content swap only — the llm pair SUPERSEDES the fast pair in the same box once the
  // deep turn completes; geometry untouched.
  const llmShort = llmDone ? shortAnswer : null;
  const tier: 'fast' | 'llm' = llmShort ? 'llm' : 'fast';
  const short = llmShort ?? fastShort ?? (!llmDone ? shortAnswer : null);
  const long =
    tier === 'llm' ? longAnswer : (fastLong ?? (!llmDone ? longAnswer : null));
  // source_path (the fast-tier caption) and narration.fast_mismatch (the "revised"
  // verdict) aren't carried by the clean view — both were already dead against the shim.
  const label = useAskLabel(tier === 'llm' ? 'answer' : 'quick_answer');

  useCardExport({
    label,
    content_key: 'ask_quick_answer',
    text: short ?? '',
    json: { short, long, tier },
    status: short ? 'loaded' : 'idle',
    // By-value card — no fetch of its own; payload truth rides the
    // ask-compose cache entry the rail turn already declares.
    query_key: null,
  });

  if (!short) return null;
  return (
    <ProseBlock
      label={label}
      body={short}
      secondary={long && long !== short ? long : undefined}
      provisional={tier === 'fast'}
    />
  );
}
